import { call, put, select, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Activity } from "@mapmycustomers/shared/types/entity";
import { MapEntry, MultiPin } from "@mapmycustomers/shared/types/map";
import Organization from "@mapmycustomers/shared/types/Organization";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import Team from "@mapmycustomers/shared/types/Team";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformListRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformListRequest";
import PlatformMapRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformMapRequest";

import localSettings from "@app/config/LocalSettings";
import { getDatesRange } from "@app/enum/dashboard/nextDateRanges";
import ViewMode from "@app/enum/dashboard/ViewMode";
import {
  getUpcomingActivitiesDrillDownTotalFilteredRecords,
  getUpcomingActivitiesDrillDownViewState,
  getUpcomingActivitiesMapBounds,
} from "@app/scene/dashboard/store/cards/upcomingActivities/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import expandColumns from "@app/scene/dashboard/utils/expandColumns";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import {
  getOrganizationId,
  getOrganizationSize,
  shouldIncludeSharedActivities,
} from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import categorizeMapEntries from "@app/util/map/categorizeMapEnties";
import getPrecisionDetails from "@app/util/map/getPrecisionDetails";
import { takeLatestBy } from "@app/util/redux/takeLatestBy";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

import {
  applyUpcomingActivitiesDrillDownListViewSettings,
  exportUpcomingActivitiesCardDrillDownData,
  fetchUpcomingActivitiesCardData,
  fetchUpcomingActivitiesCardDrillDownData,
  showUpcomingActivitiesDrillDown,
} from "./actions";

const LIST_PAGE_SIZE = 20;

const defaultColumns: string[] = [
  ActivityFieldName.PRIMARY_ASSOCIATION,
  ActivityFieldName.ACTIVITY_TYPE,
  ActivityFieldName.ASSIGNEE,
  ActivityFieldName.START_DATE,
  ActivityFieldName.END_DATE,
  ActivityFieldName.NOTE,
];

const defaultSortColumn = ActivityFieldName.COMPLETED_AT;

function* onFetchUpcomingActivitiesCardData({
  payload,
}: ReturnType<typeof fetchUpcomingActivitiesCardData>) {
  const { callback, configuration, failureCallback, offset, scope, viewMode, viewport } = payload;
  const { criteria, dateRange } = configuration;
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const organizationSize: OrganizationSize = yield select(getOrganizationSize);
    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);
    const isTeamScope = !!scope?.teamId && !scope?.userId;

    const range = getDatesRange(dateRange.range, dateRange.subRange);

    const teams: Team[] = yield select(getTeamsOfEntireOrganization);
    const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
    const assigneeIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map(({ id }) => id);

    const $and = [
      {
        ...(criteria.activityTypeIds.length
          ? {
              crmActivityTypeId: {
                $in: [...criteria.activityTypeIds],
              },
            }
          : {}),
        completed: {
          $eq: "false",
        },
        startAt: {
          $gte: range.startDate,
          $lte: range.endDate,
        },
      },
      ...(assigneeIds ? [{ assigneeId: { $in: assigneeIds } }] : []),
      ...(criteria.sources?.length ? [{ source: { $in: criteria.sources } }] : []),
    ];

    if (viewMode === ViewMode.MAP) {
      const payload: PlatformMapRequest = {
        $filters: {
          $and,
          bounds: viewport?.bounds,
          ...getPrecisionDetails(viewport?.zoom ?? 1, organizationSize),
        },
        $offset: offset,
        $order: "startAt",
      };

      const response: ListResponse<MapEntry> = yield callApi(
        "fetchMappedActivities",
        orgId,
        payload
      );
      const categorizedMapEntries = categorizeMapEntries(response.data);

      yield call(callback, {
        categorizedMapEntries,
        total: response.total,
      });
    } else {
      const activityPayload: Partial<PlatformListRequest> = {
        $filters: {
          $and,
          includeAccessStatus: true,
          includeAccountSharedActivities: includeSharedActivities,
        },
        $limit: LIST_PAGE_SIZE,
        $offset: offset,
        $order: "startAt",
      };

      const activitiesResponse: ListResponse<Activity> = yield callApi(
        "fetchActivities",
        orgId,
        activityPayload
      );

      yield call(callback, {
        activities: activitiesResponse.data,
        total: activitiesResponse.total,
      });
    }
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({ payload }: ReturnType<typeof showUpcomingActivitiesDrillDown.request>) {
  const { configuration, scope } = payload;
  const { criteria, dateRange } = configuration;

  const drillDownViewState: DrillDownViewState = yield select(
    getUpcomingActivitiesDrillDownViewState
  );
  const viewState = localSettings.getViewSettings(
    `dashboard/upcomingActivities`,
    activityFieldModel,
    {
      ...drillDownViewState,
      columns: getDefaultDrillDownColumns(defaultColumns, activityFieldModel),
      sort: [
        {
          field: activityFieldModel.getByName(defaultSortColumn)!,
          order: SortOrder.DESC,
        },
      ],
    }
  );

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const range = getDatesRange(dateRange.range, dateRange.subRange);

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const assigneeIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map(({ id }) => id);

  const filter: FilterModel = createAndCondition([
    createSimpleCondition(ActivityFieldName.COMPLETED, FilterOperator.EQUALS, "false"),
    createSimpleCondition(ActivityFieldName.START_AT, FilterOperator.IN_RANGE, [
      range.startDate,
      range.endDate,
    ]),
  ]);
  if (criteria.activityTypeIds.length) {
    filter.conditions.push(
      createSimpleCondition(
        ActivityFieldName.ACTIVITY_TYPE,
        FilterOperator.IN_ANY,
        criteria.activityTypeIds
      )
    );
  }
  if (assigneeIds) {
    filter.conditions.push(
      createSimpleCondition(ActivityFieldName.ASSIGNEE, FilterOperator.IN_ANY, assigneeIds)
    );
  }
  if (criteria.sources.length) {
    filter.conditions.push(
      createSimpleCondition(ActivityFieldName.SOURCE, FilterOperator.IN_ANY, criteria.sources)
    );
  }
  viewState.filter = filter;

  yield put(showUpcomingActivitiesDrillDown.success({ viewState }));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchUpcomingActivitiesCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: upcoming activities card, onFetchDrillDownData", payload);
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyUpcomingActivitiesDrillDownListViewSettings(payload.request));
    }

    const drillDownViewState: DrillDownViewState = yield select(
      getUpcomingActivitiesDrillDownViewState
    );

    const bounds: MultiPin["bounds"] | undefined = yield select(getUpcomingActivitiesMapBounds);

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(drillDownViewState, `dashboard/upcomingActivities`);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const requestPayload = {
      $filters: {
        bounds,
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          activityFieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const response: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      orgId,
      requestPayload
    );
    payload.dataCallback?.(response);
    yield put(
      fetchUpcomingActivitiesCardDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchUpcomingActivitiesCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onExport() {
  const drillDownViewState: DrillDownViewState = yield select(
    getUpcomingActivitiesDrillDownViewState
  );
  const total: number = yield select(getUpcomingActivitiesDrillDownTotalFilteredRecords);

  yield put(
    exportEntities.request({
      entityType: EntityType.ACTIVITY,
      total,
      viewState: expandColumns(drillDownViewState),
    })
  );
}

export function* upcomingActivitiesSagas() {
  // we don't use takeLatest because there might be several cards of such type on board
  // that's why we use takeLatestBy and filter by card id
  yield takeLatestBy(
    fetchUpcomingActivitiesCardData,
    onFetchUpcomingActivitiesCardData,
    (action: ReturnType<typeof fetchUpcomingActivitiesCardData>) => action.payload.cardId
  );
  yield takeLatest(showUpcomingActivitiesDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchUpcomingActivitiesCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportUpcomingActivitiesCardDrillDownData, onExport);
}
